// public/js/modules/ui/range-picker-modal.js
// Shared "centre date + duration slider" range picker in a Bootstrap modal, with
// a from→to timeline that marks Today. The window is centred on the chosen date,
// but NEVER extends into the future: if the after-half would pass today, it is
// clamped to today and the whole duration extends backward from there
//   to   = min(centre + duration/2, now)
//   from = to - duration
// So a centre one week ago with a 90-day window becomes [today-90d, today].
// onApply receives { fromMs, toMs, days, centreDate }.
const DAY = 86400000;
const ymd = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const windowFor = (dateStr, days, nowMs = Date.now()) => {
  const c = new Date(`${dateStr}T12:00:00`).getTime();   // noon centre avoids TZ edges
  const span = days * DAY;
  const toMs = Math.min(c + span / 2, nowMs);             // never into the future
  return { fromMs: toMs - span, toMs };
};

export function openRangePickerModal({ title = 'Select date range', hint = '', maxDays = 120, defaultDays = 14, onApply } = {}) {
  const id = 'range-picker-modal';
  document.getElementById(id)?.remove();

  const html = `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">${title}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Centre date</label>
              <input type="date" class="form-control js-rp-date" value="${ymd(Date.now())}">
            </div>
            <div class="mb-2">
              <label class="form-label mb-1">Window: <strong class="js-rp-days">${defaultDays}</strong> days
                <span class="text-muted small">(centred on the date, never past today)</span></label>
              <input type="range" class="form-range js-rp-slider" min="1" max="${maxDays}" value="${defaultDays}">
            </div>

            <!-- Timeline: window (from→to, blue), chosen date (green), today (red) -->
            <div class="position-relative mt-4" style="height:46px;">
              <div style="position:absolute; left:0; right:0; top:18px; height:8px; background:var(--bs-secondary-bg,#e9ecef); border-radius:4px;"></div>
              <div class="js-rp-fill" style="position:absolute; top:18px; height:8px; background:#0d6efd; border-radius:4px;"></div>
              <div class="js-rp-centre" style="position:absolute; top:12px; width:2px; height:20px; background:#198754;"></div>
              <div class="js-rp-today" style="position:absolute; top:12px; width:2px; height:20px; background:#dc3545;"></div>
              <div class="js-rp-today-lbl" style="position:absolute; top:-2px; transform:translateX(-50%); font-size:0.66rem; font-weight:600; color:#dc3545; white-space:nowrap;">Today</div>
              <div class="js-rp-centre-lbl" style="position:absolute; bottom:-2px; transform:translateX(-50%); font-size:0.66rem; font-weight:600; color:#198754; white-space:nowrap;"></div>
            </div>
            <div class="d-flex justify-content-between small text-muted mt-1">
              <span><span class="text-primary">●</span> From: <span class="js-rp-from"></span></span>
              <span>To: <span class="js-rp-to"></span> <span class="text-primary">●</span></span>
            </div>
            ${hint ? `<div class="form-text mt-2">${hint}</div>` : ''}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary js-rp-apply"><i class="fa-solid fa-check me-1"></i>Apply</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);

  const el = document.getElementById(id);
  const modal = new bootstrap.Modal(el);
  const dateEl = el.querySelector('.js-rp-date');
  const slider = el.querySelector('.js-rp-slider');
  const daysEl = el.querySelector('.js-rp-days');
  const fillEl = el.querySelector('.js-rp-fill');
  const centreEl = el.querySelector('.js-rp-centre');
  const centreLblEl = el.querySelector('.js-rp-centre-lbl');
  const todayEl = el.querySelector('.js-rp-today');
  const todayLblEl = el.querySelector('.js-rp-today-lbl');
  const fromEl = el.querySelector('.js-rp-from');
  const toEl = el.querySelector('.js-rp-to');

  const refresh = () => {
    daysEl.textContent = slider.value;
    if (!dateEl.value) { fromEl.textContent = toEl.textContent = ''; return; }
    const now = Date.now();
    const { fromMs, toMs } = windowFor(dateEl.value, +slider.value, now);
    const centreMs = new Date(`${dateEl.value}T12:00:00`).getTime();

    // Track spans [from … max(to, now)] so Today always sits on it.
    const trackStart = fromMs;
    const trackEnd = Math.max(toMs, now);
    const span = Math.max(1, trackEnd - trackStart);
    const pct = (ms) => Math.max(0, Math.min(100, ((ms - trackStart) / span) * 100));
    const clampLbl = (p) => Math.max(8, Math.min(92, p));

    fillEl.style.left = '0%';
    fillEl.style.width = `${pct(toMs)}%`;

    const cp = pct(centreMs);
    centreEl.style.left = `${cp}%`;
    centreLblEl.style.left = `${clampLbl(cp)}%`;
    centreLblEl.textContent = ymd(centreMs);

    const tp = pct(now);
    todayEl.style.left = `${tp}%`;
    todayLblEl.style.left = `${clampLbl(tp)}%`;

    fromEl.textContent = ymd(fromMs);
    toEl.textContent = ymd(toMs);
  };
  slider.addEventListener('input', refresh);
  dateEl.addEventListener('change', refresh);
  refresh();

  el.querySelector('.js-rp-apply').addEventListener('click', () => {
    if (!dateEl.value) return;
    const days = Math.min(maxDays, Math.max(1, +slider.value || defaultDays));
    const { fromMs, toMs } = windowFor(dateEl.value, days);
    modal.hide();
    onApply?.({ fromMs, toMs, days, centreDate: dateEl.value });
  });

  el.addEventListener('hidden.bs.modal', () => el.remove(), { once: true });
  modal.show();
}
